import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { ModalConsumerProps, withModal } from 'view/shared/modal'
import { DealTicketHeader } from './DealTicketHeader'
import { DealCard } from 'view/shared/card'
import { DealTicketFields } from './DealTicketFields'
import { DealTicketNarrativeBar } from './DealTicketNarrativeBar'
import { DealTicketFooter } from './DealTicketFooter'
import { AuthorisedUser, DealActions, DealDetail, NegotiationFieldMeta, ServicingType, User, UserPreference } from 'api'
import { canServicingCancel, canServicingRevertAttributes } from '../utils'
import { NotificationType } from 'store/notification'
import { createUserPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { createUserByUsernameGetterSelector, isAdminGatewayUserSelector } from 'selectors/userSelectors'
import { Translation } from 'locale'
import { createAuthorisedUserSelector } from 'selectors/connectionSelectors'

interface DealTicketContentProps extends ModalConsumerProps {
  dealDetail: DealDetail
  userPreference: UserPreference
  showCloseButton?: boolean
  isAdminUser: boolean
  onStartAmend: (dealId: string) => unknown
  onUpdateField: typeof DealActions.servicingUpdateAttributes
  onRevert: typeof DealActions.servicingRevertAttributes
  onCancelAmend: typeof DealActions.servicingCancel
  onClose?: () => unknown
  authorisedUser: AuthorisedUser
  getUser: (uid: string) => User | null
}

const mapStateToProps = createStructuredSelector({
  userPreference: createUserPreferenceSelector(),
  isAdminUser: isAdminGatewayUserSelector,
  authorisedUser: createAuthorisedUserSelector(),
  getUser: createUserByUsernameGetterSelector
})

const mapDispatchToProps = {
  onStartAmend: DealActions.servicingStartAmend,
  onUpdateField: DealActions.servicingUpdateAttributes,
  onRevert: DealActions.servicingRevertAttributes,
  onCancelAmend: DealActions.servicingCancel
}

export class DealTicketContentComponent extends React.Component<DealTicketContentProps> {
  componentWillUnmount() {
    const { dealDetail, onCancelAmend, authorisedUser } = this.props
    if (
      dealDetail.deal.servicingDetails?.servicingType.servicingType === ServicingType.SERVICING_AMEND &&
      canServicingCancel(dealDetail) &&
      dealDetail.servicingSideDetails?.lastUser === authorisedUser.username
    ) {
      onCancelAmend(dealDetail.deal.dealId)
    }
  }

  private handleClose = async () => {
    const { dealDetail, onClose, showDialog } = this.props
    if (onClose) {
      if (canServicingRevertAttributes(dealDetail)) {
        const dialogArgs = {
          type: NotificationType.Confirm,
          title: Translation.TC_BUTTON_CLOSE_TICKET,
          message: Translation.SC_CLOSE_TICKET_WITH_AMENDMENTS,
          buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
          cancelButton: Translation.TC_BUTTON_NO,
          defaultButton: Translation.TC_BUTTON_YES
        }
        const dialogResult = await showDialog(dialogArgs)

        if (dialogResult === Translation.TC_BUTTON_YES) {
          onClose()
        }
      } else {
        onClose()
      }
    }
  }

  private handleUpdateField = (value: any, field: NegotiationFieldMeta, rawValue?: any) => {
    const { dealDetail, userPreference, onUpdateField } = this.props
    const { dealId } = dealDetail.deal

    onUpdateField({
      dealId,
      field,
      updatedValue: value,
      updatedRawValue: rawValue,
      decisionMakerOrientedByNegotiationSide: true,
      meta: { haircutSign: userPreference.haircutSign },
      revertFirst: false
    })
  }

  render() {
    const { dealDetail, userPreference, isAdminUser, showCloseButton, onRevert, authorisedUser, getUser } = this.props
    const isAdminAmendAndWithOtherUser =
      dealDetail.deal.servicingDetails?.servicingType.servicingType === ServicingType.SERVICING_AMEND &&
      dealDetail.servicingSideDetails?.lastUser !== authorisedUser.username

    const amendingUser =
      isAdminAmendAndWithOtherUser && dealDetail.servicingSideDetails?.lastUser ? getUser(dealDetail.servicingSideDetails?.lastUser) : null
    return (
      <>
        <DealTicketHeader dealDetail={dealDetail} />
        <DealCard
          dealDetail={dealDetail}
          userPreference={userPreference}
          isAdminUser={isAdminUser}
          inTicket={true}
          isAdminAmendAndWithOtherUser={isAdminAmendAndWithOtherUser}
          amendingUser={amendingUser}
        />
        <DealTicketFields
          dealDetail={dealDetail}
          userPreference={userPreference}
          onUpdateField={this.handleUpdateField}
          isAdminAmendAndWithOtherUser={isAdminAmendAndWithOtherUser}
        />
        <DealTicketNarrativeBar dealDetail={dealDetail} onRevert={onRevert} isAdminAmendAndWithOtherUser={isAdminAmendAndWithOtherUser} />
        <DealTicketFooter
          dealDetail={dealDetail}
          showCloseButton={showCloseButton}
          onClose={this.handleClose}
          isAdminAmendAndWithOtherUser={isAdminAmendAndWithOtherUser}
        />
      </>
    )
  }
}

export const DealTicketContent = withModal(connect(mapStateToProps, mapDispatchToProps)(DealTicketContentComponent))
